import { useState } from 'react'
import { toast } from 'sonner'
import { useHaloUsers } from '@/features/halocrm-user/hooks'
import { useConfirm } from '@/components/common/ConfirmDialog'
import { useAddMember, useRemoveMember, useTeamMembers } from '../hooks'
import type { TeamMember } from '../types'
import { formatError } from './formatError'

export function MembersPanel({ teamId }: { teamId: number }) {
  const confirm = useConfirm()
  const membersQ = useTeamMembers(teamId)
  const add = useAddMember(teamId)
  const remove = useRemoveMember(teamId)

  if (membersQ.isLoading) return <div>Ładowanie członków…</div>
  if (!membersQ.data) return null

  const memberSet = new Set<number>(membersQ.data.map(m => m.id))

  return (
    <div className="grid grid-cols-2 gap-6">
      <CurrentMembersList
        members={membersQ.data}
        onRemove={async id => {
          const ok = await confirm({
            title: 'Usunąć członka z zespołu?',
            description: 'Operacja jest nieodwracalna.',
            confirmLabel: 'Usuń',
            variant: 'destructive',
          })
          if (ok) remove.mutate(id, { onError: e => toast.error(formatError(e)) })
        }}
      />
      <AddMemberSearch
        memberSet={memberSet}
        onAdd={id => add.mutate(id, { onError: e => toast.error(formatError(e)) })}
      />
    </div>
  )
}

function CurrentMembersList({ members, onRemove }: {
  members: TeamMember[]
  onRemove: (id: number) => void
}) {
  return (
    <div className="flex flex-col gap-2">
      <header className="text-xs uppercase tracking-wide text-slate-500">
        Członkowie zespołu ({members.length})
      </header>
      <div className="border rounded bg-white divide-y">
        {members.length === 0 && (
          <div className="px-3 py-4 text-sm text-slate-500 text-center">
            Brak członków - dodaj pierwszego z prawej.
          </div>
        )}
        {members.map(m => (
          <div key={m.id} className="flex items-center justify-between px-3 py-2 text-sm">
            <div className="flex flex-col min-w-0 flex-1">
              <span className="truncate">{m.fullName ?? `user #${m.id}`}</span>
              {m.email && <span className="text-[11px] text-slate-500 truncate">{m.email}</span>}
            </div>
            <button
              type="button"
              onClick={() => onRemove(m.id)}
              className="text-xs text-red-600 hover:text-red-800 ml-2"
            >Usuń</button>
          </div>
        ))}
      </div>
    </div>
  )
}

function AddMemberSearch({ memberSet, onAdd }: {
  memberSet: Set<number>
  onAdd: (id: number) => void
}) {
  const [search, setSearch] = useState('')
  const trimmed = search.trim()
  const usersQ = useHaloUsers({
    active: true,
    search: trimmed,
    limit: 50,
  })

  return (
    <div className="flex flex-col gap-2">
      <header className="text-xs uppercase tracking-wide text-slate-500">Dodaj członka</header>
      <input
        type="search"
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder="Wyszukaj po nazwisku lub e-mailu…"
        className="border rounded px-2 py-1.5 text-sm"
      />
      <div className="border rounded bg-white divide-y max-h-96 overflow-auto">
        {usersQ.isLoading && <div className="px-3 py-2 text-xs text-slate-500">Szukam…</div>}
        {usersQ.data && usersQ.data.items.length === 0 && trimmed && (
          <div className="px-3 py-2 text-xs text-slate-500">Brak wyników dla "{trimmed}"</div>
        )}
        {usersQ.data && !trimmed && (
          <div className="px-3 py-2 text-xs text-slate-500">
            Wpisz fragment nazwiska, żeby zobaczyć użytkowników.
          </div>
        )}
        {(usersQ.data?.items ?? []).map(u => {
          const isMember = memberSet.has(u.id)
          return (
            <div key={u.id} className="flex items-center justify-between px-3 py-2 text-sm">
              <div className="flex flex-col min-w-0 flex-1">
                <span className="truncate">{u.fullName || `user #${u.id}`}</span>
                {u.email && <span className="text-[11px] text-slate-500 truncate">{u.email}</span>}
              </div>
              {isMember ? (
                <span className="text-[11px] text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded">już w zespole</span>
              ) : (
                <button
                  type="button"
                  onClick={() => onAdd(u.id)}
                  className="text-xs bg-sky-600 text-white rounded px-2 py-1"
                >+ Dodaj</button>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
